import { webRTCAtom } from "@/Components/Board/boardAtoms";
import { getSessionTime } from "@/Contexts/useSessionTime";
import { isFastReplyModeAtom } from "@/Pages/Chat/Partials/chatAtoms";
import { CallState } from "@/types/callState";
import {
    CommitAndSaveOffer,
    CommitAndSaveOfferSource,
} from "@/types/CommitAndSaveOffer";
import { Client, Coach, None, PageProps } from "@/types/page";
import {
    PreSessionBriefing,
    PreSessionBriefingResponse,
} from "@/types/preSessionBriefing";
import { VideoData } from "@/types/VideoData";
import { sendEventToApp } from "@/utils/appCommunication";
import { getPageProps } from "@/utils/dataLayer";
import { getOpenTabsCount } from "@/utils/openTabs";
import { promiseModal } from "@/utils/promiseModal";
import { storedAtom } from "@/utils/storedAtom";
import { trackEvent } from "@intercom/messenger-js-sdk";
import * as Sentry from "@sentry/react";
import axios from "axios";
import { atom, getDefaultStore } from "jotai";
import { resetLiveExerciseStateAtom } from "./exerciseAtoms";

export const getVideoPageProps = () =>
    getPageProps<PageProps<None, Client | Coach>>();

export const callStateAtom = atom(CallState.Closed);
callStateAtom.onMount = () => {
    /**
     * If the user uses the browser back button to leave the video room, we need to treat it as a
     * video room exit.
     */
    if (!route().current("chat") && getDefaultStore().get(isVideoShowingAtom)) {
        getDefaultStore().set(hideVideoAtom);
    }
};

export const externalSessionIdAtom = atom<string | null>(null);
export const videoDataAtom = atom<VideoData | null>(null);
export const isVideoShowingAtom = atom(
    (get) =>
        !!get(videoDataAtom) &&
        get(callStateAtom) !== CallState.Closed &&
        !get(isVideoDataLoadingAtom),
);

export const isInfoBarOpenAtom = atom(false);

export const showChatAtom = atom(false);
export const showGoalsAtom = atom(false, (get, set, showGoals: boolean) => {
    if (get(showGoalsAtom) === showGoals) {
        return;
    }
    set(preparingToShareGoalsAtom, false);
    set(showGoalsAtom, showGoals);
    get(webRTCAtom).sendMessage({
        type: "showGoalsState",
        showGoals: showGoals,
    });
});
export const preparingToShareGoalsAtom = atom(false);
export const otherUserShowGoalsAtom = atom(false);
export const isSharingGoalsAtom = atom(
    (get) => get(showGoalsAtom) && get(otherUserShowGoalsAtom),
);
export const shareGoalsBoardAtom = atom(
    null,
    (get, set, showGoals: boolean) => {
        set(preparingToShareGoalsAtom, true);
        get(webRTCAtom).sendMessage({
            type: "setShowGoals",
            showGoals: showGoals,
        });
        const videoData = get(videoDataAtom);
        if (!showGoals || !videoData) {
            return;
        }
        trackEvent("goals_board_shared_in_live_session", {
            time_into_session: Math.round(getSessionTime() / 1000),
            client_id: videoData.appointment.client.id,
        });
        if (getVideoPageProps().user?.type === "coach") {
            axios.post(
                route("appointments.goals-board-used", {
                    appointment: videoData.appointment.id,
                }),
            );
            set(videoDataAtom, {
                ...videoData,
                appointment: {
                    ...videoData.appointment,
                    used_goals_board: true,
                },
            });
        }
    },
);
export const canShowGoalsAtom = atom(false);
export const activeVideoAppointmentIdAtom = atom((get) => {
    if (get(callStateAtom) !== CallState.InCall) {
        return null;
    }
    return get(videoDataAtom)?.appointment.id ?? null;
});
export const transcriptAtom = storedAtom("show-transcript", false);

/**
 * Shared so the control bar, the goals board and the live exercise panel see a
 * single emote cooldown between them instead of one each. Module scope means
 * it outlives the video room, so leaving a call has to clear it — otherwise the
 * next session opened within the cooldown window starts with a dead button.
 */
export const emoteCooldownAtom = atom(false);

export const showSettingsAtom = atom(false);
export const showControlsAtom = atom(false);

export const showAppointmentReviewModalAtom = atom(false);
export const commitAndSaveOfferModalAtom = atom<{
    offer: CommitAndSaveOffer;
    source: CommitAndSaveOfferSource;
} | null>(null);
export const appointmentReviewWaitingForCommitAndSaveAtom = atom(false);
export const commitAndSaveBannerOfferOverrideAtom = atom<
    CommitAndSaveOffer | null | undefined
>(undefined);
export const showAppointmentFeedbackModalAtom = atom(false);
export const showCoachReviewModalAtom = atom(false);
export const showSwapCoachesModalAtom = atom(false);
export const showSchedulerModalAtom = atom(false);

export const isVideoDataLoadingAtom = atom(false);
export const isVideoDataUnavailableAtom = atom(false);
isVideoDataUnavailableAtom.onMount = (set) => () => set(false);
export const loadAndShowVideoAtom = atom(
    null,
    async (
        get,
        set,
        { clientId, coachId }: { clientId: number; coachId: number },
    ) => {
        set(isVideoDataLoadingAtom, true);
        set(isFastReplyModeAtom, false);
        const user = getVideoPageProps().user;
        const openTabsCount = await getOpenTabsCount();
        if (user?.type === "coach" && openTabsCount >= 5) {
            await promiseModal({
                id: "too-many-tabs-modal",
                title: "Too many open tabs",
                content:
                    "You have multiple Strawberry.me browser tabs open! On older computers, this may result in a poor video call experience. Please close the other tabs before continuing.",
                confirmText: "Continue",
                cancelText: "",
            });
        }

        try {
            const { data } = await axios.get<VideoData>(
                route("video-room.getVideoData", {
                    clientId: clientId,
                    coachId: coachId,
                }),
            );
            set(videoDataAtom, data);
            set(callStateAtom, CallState.HairCheck);
            set(isVideoDataLoadingAtom, false);
            set(isVideoDataUnavailableAtom, false);
        } catch (_e) {
            set(isVideoDataLoadingAtom, false);
            set(isVideoDataUnavailableAtom, true);
            return;
        }
    },
);

export const hideVideoAtom = atom(null, async (get, set) => {
    const user = getVideoPageProps().user;
    const videoData = get(videoDataAtom);
    // Only ask for a review if the session lasted more than 25 minutes and the client hasn't submitted a review yet
    const sessionWasLongEnough =
        (getSessionTime() ?? Number.POSITIVE_INFINITY) > 1_000 * 60 * 25;
    const shouldAskForReview =
        user?.type === "client" &&
        sessionWasLongEnough &&
        videoData?.afterCallReviewType !== "no_review";
    const shouldExposeToCommitAndSave =
        user?.type === "client" &&
        sessionWasLongEnough &&
        !!videoData?.commitAndSaveVariant;
    if (
        user?.type === "coach" &&
        get(callStateAtom) === CallState.InCall &&
        (getSessionTime() ?? Number.POSITIVE_INFINITY) > 0 // other user joined
    ) {
        set(showAppointmentFeedbackModalAtom, true);
    }
    set(callStateAtom, CallState.Closed);
    set(externalSessionIdAtom, null);
    set(emoteCooldownAtom, false);
    set(resetLiveExerciseStateAtom);

    let commitAndSaveOfferShown = false;
    if (shouldExposeToCommitAndSave) {
        commitAndSaveOfferShown = await set(exposeToCommitAndSaveOfferAtom);
    }

    if (shouldAskForReview) {
        if (commitAndSaveOfferShown) {
            set(appointmentReviewWaitingForCommitAndSaveAtom, true);
        } else {
            set(showAppointmentReviewModalAtom, true);
        }
    }
});

const exposeToCommitAndSaveOfferAtom = atom(
    null,
    async (_get, set): Promise<boolean> => {
        try {
            const { data } = await axios.post<{
                offer: CommitAndSaveOffer | null;
            }>(route("commit-and-save.offers.store"));
            if (!data.offer) {
                return false;
            }
            set(commitAndSaveOfferModalAtom, {
                offer: data.offer,
                source: "post_session",
            });
            return true;
        } catch (error) {
            Sentry.captureException(error);
            return false;
        }
    },
);

export const handleReviewFeedbackAtom = atom(
    null,
    async (get, set, negativeFeedback: boolean) => {
        if (negativeFeedback) {
            // Only show SwapCoachesModal for sessions 1 and 2
            const currentSessionNumber =
                (get(videoDataAtom)?.totalPrevAppointmentsForPair ?? 0) + 1;
            if (currentSessionNumber <= 2) {
                set(showSwapCoachesModalAtom, true);
            }
        } else {
            const { data } = await axios.get<{
                hasFutureAppointments: boolean;
            }>(route("client.has-future-appointments"));
            sendEventToApp("requestAppStoreReview", {});
            if (!data.hasFutureAppointments) {
                set(showSchedulerModalAtom, true);
            }
        }
    },
);

export const preSessionBriefingAtom = atom<
    PreSessionBriefing | "does_not_exist" | null
>(null);
export const isFirstSessionAtom = atom<boolean | null>(null);

preSessionBriefingAtom.onMount = () => {
    const store = getDefaultStore();
    const loadData = async () => {
        const videoData = store.get(videoDataAtom);
        if (!videoData) {
            return;
        }
        const lastPreSessionBriefing = store.get(preSessionBriefingAtom);
        if (
            typeof lastPreSessionBriefing === "object" &&
            lastPreSessionBriefing?.appointmentId !== videoData.appointment.id
        ) {
            store.set(preSessionBriefingAtom, null);
            store.set(isFirstSessionAtom, null);
        }
        if (
            typeof lastPreSessionBriefing === "object" &&
            lastPreSessionBriefing !== null
        ) {
            return;
        }
        try {
            const { data } = await window.axios.get<PreSessionBriefingResponse>(
                route("appointment.preSessionBriefing", {
                    appointment: videoData.appointment.id,
                }),
            );
            store.set(
                preSessionBriefingAtom,
                data.preSessionBriefing ?? "does_not_exist",
            );
            store.set(isFirstSessionAtom, data.isFirstSession);
        } catch {
            store.set(preSessionBriefingAtom, "does_not_exist");
            store.set(isFirstSessionAtom, null);
        }
    };
    loadData();
    return () => {
        store.set(preSessionBriefingAtom, null);
        store.set(isFirstSessionAtom, null);
    };
};
